import { useEffect, useRef } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import AutocompleteInput from "@/components/shared/AutocompleteInput";
import FormTable, { ComplaintsTableColumns } from "@/components/shared/FormTable";

// Enhanced complaint options with better medical organization
const COMPLAINT_OPTIONS = [
  "Fever", "Headache", "Cough (Dry)", "Cough (Productive)", "Chest Pain", "Abdominal Pain", "Back Pain", "Joint Pain", "Sore Throat", "Nausea", "Vomiting", "Diarrhea", "Constipation", "Fatigue", "Dizziness", "Shortness of Breath", "Palpitations", "Loss of Appetite", "Weight Loss", "Sleep Problems", "Skin Rash", "Muscle Pain", "Eye Pain", "Ear Pain", "Runny Nose", "Body Ache"
];

// Enhanced frequency options with medical context
const FREQUENCY_OPTIONS = [
  "Continuous", "Multiple times daily", "3-4 times daily", "Twice daily", "Once daily", "Occasionally", "At night only", "In morning only", "After meals", "Before meals", "During sleep", "On exertion"
];

// Professional severity options
const SEVERITY_OPTIONS = [
  "Mild",
  "Moderate", 
  "Severe",
  "Very severe"
];

// Enhanced duration suggestions with medical context
const DURATION_OPTIONS = [
  "since morning",
  "since yesterday", 
  "since last week",
  "since last night",
  "1 day",
  "2 days",
  "3 days", 
  "1 week",
  "2 weeks",
  "1 month",
  "2 months",
  "3 months",
  "for few days",
  "for 1 week",
  "about 1 week",
  "about 2 weeks",
  "intermittent",
  "chronic"
];

// Smart date calculation helper
const calculateDateFromDuration = (duration: string): string => {
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  
  if (!duration) return "";
  
  const durationLower = duration.toLowerCase().trim();
  
  // Handle "since" patterns
  if (durationLower.includes("since morning") || durationLower.includes("since today")) {
    return todayStr;
  }
  
  if (durationLower.includes("since yesterday")) {
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  }
  
  if (durationLower.includes("since last week")) {
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);
    return lastWeek.toISOString().split('T')[0];
  }
  
  // Handle specific day patterns
  const dayMatch = RegExp(/(\d+)\s*(day|days)/).exec(durationLower);
  if (dayMatch) {
    const days = Number.parseInt(dayMatch[1]);
    const pastDate = new Date(today);
    pastDate.setDate(pastDate.getDate() - days);
    return pastDate.toISOString().split('T')[0];
  }
  
  // Handle week patterns
  const weekMatch = durationLower.match(/(\d+)\s*(week|weeks)/);
  if (weekMatch) {
    const weeks = Number.parseInt(weekMatch[1]);
    const pastDate = new Date(today);
    pastDate.setDate(pastDate.getDate() - (weeks * 7));
    return pastDate.toISOString().split('T')[0];
  }
  
  // Handle month patterns  
  const monthMatch = durationLower.match(/(\d+)\s*(month|months)/);
  if (monthMatch) {
    const months = Number.parseInt(monthMatch[1]);
    const pastDate = new Date(today);
    pastDate.setMonth(pastDate.getMonth() - months);
    return pastDate.toISOString().split('T')[0];
  }
  
  return "";
};

// Enterprise-level dynamic placeholders
const getDynamicPlaceholder = (columnKey: string, index: number): string => {
  const placeholders = {
    complaint: ["Primary complaint...", "Secondary complaint...", "Additional symptom...", "Other concern...", "Associated symptom...", "Related complaint..."],
    frequency: ["How often?", "Frequency...", "Occurrence...", "Pattern...", "Timing...", "Schedule..."],
    severity: ["Intensity?", "Severity...", "Grade...", "Level...", "Degree...", "Scale..."],
    duration: ["Since when?", "Duration...", "Time period...", "How long?", "Timeline...", "Started..."],
    date: ["Select date", "When started", "Onset date", "Select date", "Date noticed", "Start date"]
  };
  
  const options = placeholders[columnKey] || [columnKey];
  return options[Math.min(index, options.length - 1)];
};

const DEFAULT_ROW = {
  complaint: "",
  frequency: "",
  severity: "",
  duration: "",
  date: ""
};

export default function ComplaintsGrid() {
  const {
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext();

  const { fields, append, remove } = useFieldArray({ 
    control, 
    name: "complaints"
  });
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  // Initialize with empty row if needed
  useEffect(() => {
    if (fields.length === 0) {
      append(DEFAULT_ROW);
    }
  }, [fields.length, append]);

  // Simple complaint change handler
  const handleComplaintChange = (index: number, complaint: string) => {
    setValue(`complaints.${index}.complaint`, complaint, { shouldDirty: true });
  };

  // Enhanced duration change with smart date calculation
  const handleDurationChange = (index: number, duration: string) => {
    setValue(`complaints.${index}.duration`, duration, { shouldDirty: true });
    
    // Auto-calculate date based on duration
    if (duration && duration.trim() !== "") {
      const calculatedDate = calculateDateFromDuration(duration);
      if (calculatedDate) {
        setValue(`complaints.${index}.date`, calculatedDate, { shouldDirty: true });
      }
    } else {
      // Clear date when duration is empty
      setValue(`complaints.${index}.date`, "", { shouldDirty: true });
    }
  };

  const focusNextField = (index: number, fieldOffset: number) => {
    const nextIndex = index * 5 + fieldOffset;
    if (refs.current[nextIndex]) {
      setTimeout(() => refs.current[nextIndex]?.focus(), 50);
    }
  };

  // Compact cell rendering for better mobile experience
  const renderCell = (field: any, columnKey: string, index: number) => {
    const baseInputClass = cn(
      "h-7 px-2 text-xs border border-input rounded-md bg-background transition-all duration-200",
      "focus:border-ring focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0",
      "hover:border-ring/60",
      "placeholder:text-muted-foreground placeholder:font-normal"
    );

    const errorInputClass = cn(
      baseInputClass,
      "border-destructive/30 bg-destructive/5 focus:border-destructive focus-visible:ring-0"
    );

    switch (columnKey) {
      case 'complaint':
        return (
          <AutocompleteInput
            value={getValues(`complaints.${index}.complaint`) || ""}
            onChange={(val) => handleComplaintChange(index, val)}
            options={COMPLAINT_OPTIONS}
            placeholder={getDynamicPlaceholder('complaint', index)}
            onEnterNext={() => focusNextField(index, 1)}
            className={errors.complaints?.[index]?.complaint ? errorInputClass : baseInputClass}
            showAllOnFocus={true}
            dropdownMinWidth={200}
            ref={(el) => {
              refs.current[index * 5] = el;
            }}
          />
        );

      case 'frequency':
        return (
          <AutocompleteInput
            value={getValues(`complaints.${index}.frequency`) || ""}
            onChange={(val) => setValue(`complaints.${index}.frequency`, val, { shouldDirty: true })}
            options={FREQUENCY_OPTIONS}
            placeholder={getDynamicPlaceholder('frequency', index)}
            onEnterNext={() => focusNextField(index, 2)}
            className={errors.complaints?.[index]?.frequency ? errorInputClass : baseInputClass}
            showAllOnFocus={true}
            dropdownMinWidth={150}
            ref={(el) => {
              refs.current[index * 5 + 1] = el;
            }}
          />
        );

      case 'severity':
        return (
          <AutocompleteInput
            value={getValues(`complaints.${index}.severity`) || ""}
            onChange={(val) => setValue(`complaints.${index}.severity`, val, { shouldDirty: true })}
            options={SEVERITY_OPTIONS}
            placeholder={getDynamicPlaceholder('severity', index)}
            onEnterNext={() => focusNextField(index, 3)}
            className={errors.complaints?.[index]?.severity ? errorInputClass : baseInputClass}
            showAllOnFocus={true}
            dropdownMinWidth={100}
            ref={(el) => {
              refs.current[index * 5 + 2] = el;
            }}
          />
        );

      case 'duration':
        return (
          <AutocompleteInput
            value={getValues(`complaints.${index}.duration`) || ""}
            onChange={(val) => handleDurationChange(index, val)}
            options={DURATION_OPTIONS}
            placeholder={getDynamicPlaceholder('duration', index)}
            onEnterNext={() => focusNextField(index, 4)}
            className={errors.complaints?.[index]?.duration ? errorInputClass : baseInputClass}
            showAllOnFocus={true}
            dropdownMinWidth={140}
            ref={(el) => {
              refs.current[index * 5 + 3] = el;
            }}
          />
        );

      case 'date':
        return (
          <Input
            id={`complaint-date-${index}`}
            name={`complaint-date-${index}`}
            value={getValues(`complaints.${index}.date`) || ""}
            onChange={(e) => setValue(`complaints.${index}.date`, e.target.value, { shouldDirty: true })}
            type="date"
            className={cn(
              errors.complaints?.[index]?.date ? errorInputClass : baseInputClass,
              "text-xs [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:hover:opacity-100 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:scale-90"
            )}
            ref={(el) => {
              refs.current[index * 5 + 4] = el;
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                const nextRow = (index + 1) * 5;
                if (refs.current[nextRow]) {
                  refs.current[nextRow]?.focus();
                } else {
                  append(DEFAULT_ROW);
                  setTimeout(() => refs.current[nextRow]?.focus(), 50);
                }
              }
            }}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="enterprise-complaints-grid">
      <FormTable
        title="Chief Complaints"
        columns={ComplaintsTableColumns}
        fields={fields}
        errors={errors}
        renderCell={renderCell}
        onAddRow={() => append(DEFAULT_ROW)}
        onRemoveRow={(index) => remove(index)}
        canRemoveRow={() => fields.length > 1}
        addButtonText="Add Complaint"
        showRowNumbers={true}
        autoAddRow={true}
        primaryField="complaint"
      />
    </div>
  );
}